import React, { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { isGDPRCountry } from '@mc/gdpr'
import { useGeo } from '@mc/geo-react'
import {
  useFireEvent,
  educatorPageNames,
  fireCartEmailCaptured,
} from '../tracking/educatorTracking'
import { useUser } from './AuthContext'

export const US_FIRST_LOGIN_CONSENT_FIRED_KEY =
  'educator_us_first_login_marketing_consent_fired'

/**
 * For US users who land on the app after login (e.g. from /login), fire
 * educatorCartEmailCaptured once and set a localStorage flag. Does not run when
 * coming from /auth/post-login (that page handles social login).
 */
export const MarketingConsentProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const router = useRouter()
  const { user, isLoading } = useUser()
  const fireEvent = useFireEvent()
  const { country } = useGeo()
  const firedRef = useRef(false)

  const isPostLoginPage = router.pathname.includes('/auth/post-login')

  useEffect(() => {
    if (isLoading || !user?.email || firedRef.current) return

    // GDPR/opt-in check: GDPR countries + unknown (XX) require explicit consent
    const isGdpr = country === 'XX' || isGDPRCountry(country ?? '')

    if (isGdpr || isPostLoginPage) return

    const alreadyFired =
      typeof window !== 'undefined' &&
      window.localStorage.getItem(US_FIRST_LOGIN_CONSENT_FIRED_KEY)
    if (alreadyFired) return

    const userEmail = user.email // Capture email for async closure

    ;(async () => {
      await fireCartEmailCaptured(fireEvent, {
        product_name: 'educator',
        page: educatorPageNames.EMAIL_ENTRY,
        email: userEmail,
        location: 'Educator Registration',
        marketing_consent: true,
        email_capture_method: 'user_added',
        gdpr_opt_in: false,
      })
    })()
    window.localStorage.setItem(US_FIRST_LOGIN_CONSENT_FIRED_KEY, '1')
    firedRef.current = true
  }, [isLoading, user, country, isPostLoginPage, fireEvent])

  return <>{children}</>
}
